if (!customElements.get('vl-pdp-memory-carousel')) {
  class VLPDPMemoryCarousel extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.isInitialized = false;
      this.abortController = null;
      this.resizeObserver = null;
      this.pointerState = null;
      this.instantFrame = null;

      this.handlePreviousClick = this.handlePreviousClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handlePaginationClick = this.handlePaginationClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerEnd = this.handlePointerEnd.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.preventNativeDrag = this.preventNativeDrag.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-vl-pdp-memory-carousel-viewport]');
      this.track = this.querySelector('[data-vl-pdp-memory-carousel-track]');
      this.inner = this.querySelector('.vl-pdp-memory-carousel__inner');
      this.slides = [...this.querySelectorAll('[data-vl-pdp-memory-carousel-slide]')];
      this.paginationItems = [
        ...this.querySelectorAll('[data-vl-pdp-memory-carousel-pagination]'),
      ];
      this.previousButton = this.querySelector('[data-vl-pdp-memory-carousel-previous]');
      this.nextButton = this.querySelector('[data-vl-pdp-memory-carousel-next]');
      this.status = this.querySelector('[data-vl-pdp-memory-carousel-status]');

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.previousButton?.addEventListener('click', this.handlePreviousClick, { signal });
      this.nextButton?.addEventListener('click', this.handleNextClick, { signal });
      this.addEventListener('click', this.handlePaginationClick, { signal });
      this.viewport.addEventListener('keydown', this.handleKeydown, { signal });
      this.viewport.addEventListener('pointerdown', this.handlePointerDown, { signal });
      this.viewport.addEventListener('pointermove', this.handlePointerMove, { signal });
      this.viewport.addEventListener('pointerup', this.handlePointerEnd, { signal });
      this.viewport.addEventListener('pointercancel', this.handlePointerEnd, { signal });
      this.viewport.addEventListener('dragstart', this.preventNativeDrag, { signal });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.handleResize, { signal });
      }

      const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.showSlide(initialIndex >= 0 ? initialIndex : 0, false, false);
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);

      this.abortController = null;
      this.resizeObserver = null;
      this.pointerState = null;
      this.instantFrame = null;
      this.isInitialized = false;
    }

    handlePreviousClick() {
      this.showSlide(this.currentIndex - 1);
    }

    handleNextClick() {
      this.showSlide(this.currentIndex + 1);
    }

    handlePaginationClick(event) {
      const item = event.target.closest('[data-vl-pdp-memory-carousel-pagination]');
      if (!item || !this.contains(item)) return;

      this.showSlide(Number(item.dataset.slideIndex));
    }

    handleKeydown(event) {
      if (event.target !== this.viewport) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePreviousClick();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNextClick();
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.showSlide(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.showSlide(this.slides.length - 1);
      }
    }

    handlePointerDown(event) {
      if (this.slides.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointerState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        currentY: event.clientY,
        startTime: performance.now(),
        axis: null,
        baseOffset: this.getTargetOffset(this.currentIndex),
      };

      this.viewport.setPointerCapture?.(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      this.pointerState.currentX = event.clientX;
      this.pointerState.currentY = event.clientY;

      const deltaX = event.clientX - this.pointerState.startX;
      const deltaY = event.clientY - this.pointerState.startY;

      if (!this.pointerState.axis && (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6)) {
        this.pointerState.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }

      if (this.pointerState.axis !== 'x') return;

      event.preventDefault();
      this.classList.add('is-dragging');

      const isPastStart = this.currentIndex === 0 && deltaX > 0;
      const isPastEnd = this.currentIndex === this.slides.length - 1 && deltaX < 0;
      const resistedDelta = isPastStart || isPastEnd ? deltaX * 0.28 : deltaX;

      this.setTrackOffset(this.pointerState.baseOffset - resistedDelta);
    }

    handlePointerEnd(event) {
      if (!this.pointerState || event.pointerId !== this.pointerState.pointerId) return;

      const pointerState = this.pointerState;
      const endX = Number.isFinite(event.clientX) ? event.clientX : pointerState.currentX;
      const endY = Number.isFinite(event.clientY) ? event.clientY : pointerState.currentY;
      const deltaX = endX - pointerState.startX;
      const deltaY = endY - pointerState.startY;
      const duration = Math.max(performance.now() - pointerState.startTime, 1);
      const velocity = Math.abs(deltaX) / duration;
      const threshold = Math.min(72, Math.max(32, this.slides[this.currentIndex].clientWidth * 0.12));
      const isHorizontal = pointerState.axis === 'x' && Math.abs(deltaX) > Math.abs(deltaY);
      const shouldAdvance = Math.abs(deltaX) >= threshold || velocity >= 0.55;

      if (this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      this.pointerState = null;
      this.classList.remove('is-dragging');

      if (event.type !== 'pointercancel' && isHorizontal && shouldAdvance) {
        this.showSlide(this.currentIndex + (deltaX < 0 ? 1 : -1));
      } else {
        this.updatePosition(true);
      }
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;

      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index === -1) return;

      this.showSlide(index, false, false);
    }

    handleResize() {
      this.updatePosition(false);
    }

    preventNativeDrag(event) {
      event.preventDefault();
    }

    showSlide(index, animate = true, announce = true) {
      const targetIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const shouldAnimate = animate && !this.prefersReducedMotion;

      this.currentIndex = targetIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === targetIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.paginationItems.forEach((item, itemIndex) => {
        const isActive = itemIndex === targetIndex;
        item.classList.toggle('is-active', isActive);

        if (isActive) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      if (this.previousButton) this.previousButton.disabled = targetIndex === 0;
      if (this.nextButton) this.nextButton.disabled = targetIndex === this.slides.length - 1;

      this.updatePosition(shouldAnimate);

      if (announce && this.status) {
        this.status.textContent = `Showing slide ${targetIndex + 1} of ${this.slides.length}`;
      }
    }

    updatePosition(animate = true) {
      if (!animate) {
        this.classList.add('is-instant');
        if (this.instantFrame !== null) cancelAnimationFrame(this.instantFrame);
      }

      this.setTrackOffset(this.getTargetOffset(this.currentIndex));

      if (!animate) {
        this.instantFrame = requestAnimationFrame(() => {
          this.classList.remove('is-instant');
          this.instantFrame = null;
        });
      }
    }

    getTargetOffset(index) {
      const slide = this.slides[index];
      if (!slide) return 0;

      const slideRect = slide.getBoundingClientRect();
      const trackRect = this.track.getBoundingClientRect();
      const slideOffset = slideRect.left - trackRect.left;

      if (index !== this.slides.length - 1) return slideOffset;

      const contentWidth =
        this.inner?.getBoundingClientRect().width || this.viewport.getBoundingClientRect().width;
      return Math.max(0, slideOffset + slideRect.width - contentWidth);
    }

    setTrackOffset(offset) {
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('vl-pdp-memory-carousel', VLPDPMemoryCarousel);
}
